import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, AlertCircle, X, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { 
  formatPhoneNumber, 
  validatePhoneNumber, 
  detectCountry, 
  cleanPhoneNumber,
  COUNTRY_CODES,
  type CountryCode 
} from "@/utils/phoneUtils";

interface PhoneInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface ValidationState {
  isValid: boolean;
  message: string;
  status: "idle" | "validating" | "valid" | "invalid";
  country?: CountryCode;
}

export function PhoneInput({
  id,
  label,
  value,
  onChange,
  required = false,
  placeholder,
  className = "",
  disabled = false
}: PhoneInputProps) {
  const [validation, setValidation] = useState<ValidationState>({
    isValid: true,
    message: "",
    status: "idle"
  });
  
  const [touched, setTouched] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(COUNTRY_CODES[0]); // Default to Italy
  const [searchQuery, setSearchQuery] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedValue = useDebounce(value, 200);
  
  // Auto-detect country and extract phone number when value changes
  useEffect(() => {
    if (value) {
      const country = detectCountry(value);
      if (country) {
        setSelectedCountry(country);
        
        // Robust phone number extraction
        const countryCode = country.code; // Already includes "+"
        let phoneNumberPart = "";
        
        if (value.startsWith(countryCode)) {
          // Extract everything after the country code
          phoneNumberPart = value.slice(countryCode.length).trim();
        } else {
          // Fallback: try to find and remove any country code pattern
          const cleanValue = cleanPhoneNumber(value);
          if (cleanValue.startsWith(countryCode.replace('+', ''))) {
            phoneNumberPart = cleanValue.slice(countryCode.replace('+', '').length);
          } else {
            phoneNumberPart = cleanValue;
          }
        }
        
        // Ensure we only set the phone number part (no country code)
        setPhoneNumber(phoneNumberPart);
      } else {
        // No country detected, treat as raw phone number
        const cleanValue = cleanPhoneNumber(value);
        setPhoneNumber(cleanValue);
      }
    } else {
      setPhoneNumber("");
    }
  }, [value]);

  // Instant validation for basic checks
  useEffect(() => {
    // Instant feedback for required fields and basic validation
    if (required && !value.trim()) {
      setValidation({ isValid: false, message: `${label} is required`, status: "invalid" });
      return;
    }

    if (value === "") {
      setValidation({ isValid: true, message: "", status: "idle" });
      return;
    }

    if (value.trim() && value.length < 5) {
      setValidation({ isValid: false, message: "Phone number too short", status: "invalid" });
      return;
    }

    // Show validating state for longer inputs while debounced validation runs
    if (value.trim() && value.length >= 5) {
      setValidation({ isValid: false, message: "Validating...", status: "validating" });
    }
  }, [value, required, label]);

  // Debounced validation for comprehensive checks
  useEffect(() => {
    const validate = () => {
      if (debouncedValue === "") {
        // Don't override instant validation for empty required fields
        if (required) return;
        setValidation({ isValid: true, message: "", status: "idle" });
        return;
      }

      // Skip validation if already showing instant feedback
      if (required && !debouncedValue.trim()) return;
      if (debouncedValue.trim() && debouncedValue.length < 5) return;

      let result: { isValid: boolean; message: string; country?: CountryCode } = { isValid: true, message: "" };

      if (debouncedValue.trim()) {
        result = validatePhoneNumber(debouncedValue);
      }

      setValidation({
        isValid: result.isValid,
        message: result.message,
        status: result.isValid ? "valid" : "invalid",
        country: result.country
      });
    };

    validate();
  }, [debouncedValue, required, label]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Only allow digits, spaces, dashes, parentheses
    const sanitized = inputValue.replace(/[^0-9\s\-\(\)]/g, '');
    
    // Update internal phone number state
    setPhoneNumber(sanitized);
    
    // Immediately update parent with the complete value for real-time validation
    const fullValue = sanitized ? `${selectedCountry?.code || ''} ${sanitized}`.trim() : '';
    onChange(fullValue);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const formatted = formatPhoneNumber(pastedText);
    onChange(formatted);
  };

  const handleBlur = () => {
    setTouched(true);
    
    // Send complete formatted value to parent on blur
    if (selectedCountry && phoneNumber) {
      const fullValue = `${selectedCountry.code} ${phoneNumber}`;
      onChange(fullValue);
    } else if (phoneNumber) {
      onChange(phoneNumber);
    }
  };

  const handleCountrySelect = (country: CountryCode) => {
    setSelectedCountry(country);
    setIsCountryOpen(false);
    setSearchQuery("");
    
    // Construct new value with new country code + current phone number
    const newValue = phoneNumber ? `${country.code} ${phoneNumber}` : `${country.code}`;
    onChange(newValue);
    
    // Focus input after selection
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const getStatusIcon = () => {
    switch (validation.status) {
      case "validating":
        return <div className="animate-spin h-4 w-4 border-2 border-muted-foreground border-t-transparent rounded-full" />;
      case "valid":
        return <Check className="h-4 w-4 text-success" />;
      case "invalid":
        return <X className="h-4 w-4 text-destructive" />;
      default:
        return null;
    }
  };

  const getInputClassName = () => {
    let baseClassName = cn("rounded-l-none border-l-0", className);
    
    if (validation.status === "valid") {
      baseClassName += " border-success focus-visible:ring-success";
    } else if (validation.status === "invalid") {
      baseClassName += " border-destructive focus-visible:ring-destructive";
    }
    
    return baseClassName;
  };

  const getCountryCodeClassName = () => {
    let baseClassName = "h-10 px-3 bg-muted/30 border border-r-0 rounded-l-md flex items-center gap-2 text-sm font-medium min-w-0";
    
    if (validation.status === "valid") {
      baseClassName += " border-success";
    } else if (validation.status === "invalid") {
      baseClassName += " border-destructive";
    } else {
      baseClassName += " border-input";
    }
    
    return baseClassName;
  };

  // Extract phone number part from full value (legacy support)
  const getPhoneNumberValue = () => {
    return phoneNumber;
  };

  const getPlaceholderText = () => {
    if (placeholder) return placeholder;
    if (selectedCountry) return selectedCountry.example;
    return "+39 123 456 7890";
  };

  const getFormatHint = () => {
    if (selectedCountry) {
      return `Format: ${selectedCountry.format.replace(/X/g, '0')}`;
    }
    return "Select a country and enter your phone number";
  };

  const filteredCountries = COUNTRY_CODES.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.includes(searchQuery)
  );

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      
      <div className="relative flex">
        {/* Protected Country Code Section */}
        <div className={getCountryCodeClassName()}>
          <Popover open={isCountryOpen} onOpenChange={setIsCountryOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1 gap-1 hover:bg-background/50 focus:ring-1 bg-transparent"
                disabled={disabled}
              >
                <span className="text-base">
                  {selectedCountry?.flag || "🌍"}
                </span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0 bg-background border shadow-lg" align="start" sideOffset={4}>
              <div className="p-2 border-b bg-background">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search countries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-8 bg-background"
                  />
                </div>
              </div>
              <div className="max-h-64 overflow-auto">
                {filteredCountries.map((country) => (
                  <button
                    key={`${country.code}-${country.name}`}
                    className="w-full px-3 py-2.5 text-left hover:bg-muted/70 flex items-center gap-3 transition-colors"
                    onClick={() => handleCountrySelect(country)}
                  >
                    <span className="text-base">{country.flag}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{country.name}</div>
                      <div className="text-xs text-muted-foreground">{country.code}</div>
                    </div>
                  </button>
                ))}
                {filteredCountries.length === 0 && (
                  <div className="p-3 text-center text-sm text-muted-foreground">
                    No countries found
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Display the protected country code */}
          <span className="text-foreground/70">
            {selectedCountry?.code || ''}
          </span>
        </div>

        {/* Editable Phone Number Input */}
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            id={id}
            type="tel"
            value={phoneNumber}
            onChange={handleChange}
            onPaste={handlePaste}
            onBlur={handleBlur}
            placeholder={selectedCountry ? selectedCountry.example.replace(/^\+\d+\s/, '') : "Enter phone number"}
            className={getInputClassName()}
            required={required}
            disabled={disabled}
          />
          
          {validation.status !== "idle" && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {getStatusIcon()}
            </div>
          )}
        </div>
      </div>
      
      {/* Validation message */}
      {validation.message && (
        <div className={`flex items-center gap-2 text-sm ${
          validation.status === "invalid" ? "text-destructive" : "text-muted-foreground"
        }`}>
          {validation.status === "invalid" && <AlertCircle className="h-4 w-4" />}
          <span>{validation.message}</span>
        </div>
      )}
      
      {/* Format hint - only show when no validation message and field is empty */}
      {!validation.message && value === "" && (
        <div className="text-xs text-muted-foreground">
          {getFormatHint()}
        </div>
      )}
    </div>
  );
}